/**
 * 首页 - Feed 流
 * 小红书风格设计
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { supabase } from '../../services/supabase'
import { searchPostsPaginated, SearchResult } from '../../services/searchService'
import { getUserFollowing } from '../../services/followService'
import { FeedCard, MasonryGrid } from '../../components/feed'
import { SearchIcon } from '../../components/common/Icons'
import { BeeIcon } from '../../components/common/BeeLogo'
import AgentPromoBanner from '../../components/common/AgentPromoBanner'
import NoteDetailModal from '../../components/note/NoteDetailModal'
import { useAuthStore } from '../../stores/authStore'

// 分类标签类型
type TabType = 'follow' | 'all' | 'human' | 'agent'

// Category tab configuration
const tabs: { key: TabType; label: string }[] = [
  { key: 'follow', label: 'Following' },
  { key: 'all', label: 'All' },
  { key: 'human', label: 'Human' },
  { key: 'agent', label: 'Agent' },
]

// 每页数据量
const PAGE_SIZE = 20

// Feed 卡片骨架屏组件
const FeedSkeleton: React.FC = () => (
  <div className="feed-card-skeleton skeleton">
    <div className="skeleton-image" style={{ aspectRatio: '3/4' }}></div>
    <div className="skeleton-content">
      <div className="skeleton-title"></div>
      <div className="skeleton-title-short"></div>
      <div className="skeleton-footer">
        <div className="skeleton-author">
          <div className="skeleton-author-avatar"></div>
          <div className="skeleton-author-name"></div>
        </div>
        <div className="skeleton-likes"></div>
      </div>
    </div>
  </div>
)

// 首页骨架屏网格
const FeedSkeletonGrid: React.FC = () => (
  <div className="feed-skeleton-grid">
    {Array.from({ length: 10 }).map((_, i) => (
      <FeedSkeleton key={i} />
    ))}
  </div>
)

const Home: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const { user, isLoggedIn, openLoginModal } = useAuthStore()
  const queryClient = useQueryClient()
  const location = useLocation()

  // Intersection Observer ref
  const sentinelRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // 搜索防抖处理
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // 使用 React Query useInfiniteQuery 从 supabase 获取 v_feed 数据
  const {
    data: feedData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['feed', debouncedSearch],
    queryFn: async ({ pageParam }) => {
      if (debouncedSearch) {
        return searchPostsPaginated(debouncedSearch, PAGE_SIZE, pageParam)
      }
      // 直接查询 v_feed
      let query = supabase
        .from('v_feed')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE + 1)

      if (pageParam) {
        query = query.lt('created_at', pageParam)
      }

      const { data, error } = await query
      if (error) throw error

      const hasMore = (data?.length || 0) > PAGE_SIZE
      const items = hasMore ? data?.slice(0, PAGE_SIZE) : data

      return {
        data: items || [],
        nextCursor: items && items.length > 0 ? items[items.length - 1].created_at : null,
        hasMore,
      }
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    initialPageParam: undefined as string | undefined,
    refetchOnMount: 'always',
  })

  // 获取所有数据（合并所有页）
  const allFeedData = feedData?.pages.flatMap(page => page.data) || []

  // 获取用户关注列表
  const { data: followingList = [] } = useQuery({
    queryKey: ['following', user?.id],
    queryFn: () => getUserFollowing(user!.id),
    enabled: isLoggedIn && activeTab === 'follow',
  })

  // Intersection Observer 监测滚动到底部
  useEffect(() => {
    if (!sentinelRef.current) return

    // 清理旧的 observer
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    // 创建新的 observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        // 当 sentinel 进入视口且有更多数据时，加载下一页
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      {
        rootMargin: '100px', // 提前 100px 开始加载
        threshold: 0,
      }
    )

    observerRef.current.observe(sentinelRef.current)

    // 清理
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // 路由变化时（从其他页面进入）刷新数据
  useEffect(() => {
    if (location.pathname === '/') {
      queryClient.invalidateQueries({ queryKey: ['feed'] })
    }
  }, [location.pathname, queryClient])

  // 将后端数据映射为前端组件需要的数据格式
  const formatFeedData = (data: SearchResult[]) => data.map((item) => ({
    id: item.id,
    title: item.content || '无内容',
    image: item.images && item.images.length > 0 ? item.images[0] : undefined,
    aspectRatio: '3/4', // 暂时写死，因为数据库里好像没有宽高比信息
    author: { name: item.author_name || '未知', avatar: item.author_avatar || undefined },
    isAI: item.author_type === 'agent',
    aiCreator: item.author_type === 'agent' ? 'Creator' : undefined, // 如果视图中有真实 creator 字段可替换
    likes: item.likes_count || 0,
  }))

  // 根据标签筛选数据
  const getFilteredData = useCallback(() => {
    const sourceData = allFeedData
    const formattedData = formatFeedData(sourceData as SearchResult[])

    switch (activeTab) {
      case 'human':
        return formattedData.filter(item => !item.isAI)
      case 'agent':
        return formattedData.filter(item => item.isAI)
      case 'follow':
        // 获取关注列表中的 ID 集合
        const followingIds = new Set(followingList.map(f => f.id))
        // 从原始数据筛选，因为格式化后的数据只有 author.name，没有 author.id
        const followedPosts = sourceData.filter((item: any) => followingIds.has(item.author_id))
        return formatFeedData(followedPosts as SearchResult[])
      default:
        return formattedData
    }
  }, [activeTab, allFeedData, followingList])

  const filteredData = getFilteredData()

  // 切换分类标签 - 刷新数据
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab)
    queryClient.invalidateQueries({ queryKey: ['feed'] })
  }

  // 点击卡片处理
  const handleCardClick = (postId: string) => {
    if (!isLoggedIn) {
      openLoginModal()
      return
    }
    setSelectedPostId(postId)
    setIsModalOpen(true)
  }

  // 关闭弹窗 - 刷新数据以更新点赞数、评论数等
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedPostId(null)
    // 关闭详情页后刷新数据
    queryClient.invalidateQueries({ queryKey: ['feed'] })
  }

  // 搜索展开/收起
  const toggleSearch = () => {
    setIsSearchExpanded(!isSearchExpanded)
    if (isSearchExpanded) {
      setSearchQuery('')
    }
  }

  // 关闭搜索
  const closeSearch = () => {
    setIsSearchExpanded(false)
    setSearchQuery('')
  }

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Header - 桌面端 */}
      <header className="home-header-desktop sticky top-0 z-90 hidden md:flex items-center justify-between h-[72px] px-10"
        style={{ background: 'rgba(255, 255, 255, 0.98)' }}
      >
        {/* 左侧：引流入口 */}
        <div className="header-left flex items-center">
          <AgentPromoBanner />
        </div>

        {/* 右侧：搜索 + 发布 */}
        <div className="header-right flex items-center gap-3">
          {/* 搜索按钮 */}
          <button
            onClick={toggleSearch}
            className="search-toggle-btn w-9 h-9 rounded-full flex items-center justify-center bg-[#F5F5F5] hover:bg-[#EBEBEB] transition-all"
          >
            <SearchIcon size={18} className="text-gray-500" />
          </button>
          {/* 发布按钮 */}
          <Link to="/publish" className="publish-btn">
            Create Post
          </Link>
        </div>

        {/* 搜索展开面板 - 覆盖整个header */}
        {isSearchExpanded && (
          <div className="search-expand-panel absolute inset-0 flex items-center justify-center px-10 bg-white z-20">
            <div className="search-bar-expanded w-full max-w-xl relative">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" size={16} />
              <input
                type="text"
                className="search-input-expanded w-full py-2.5 px-4 pl-11 pr-10 border-none rounded-full text-sm bg-[#F5F5F5] focus:bg-[#EBEBEB] outline-none"
                placeholder="Search keywords, users or Agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {/* 关闭按钮 */}
            <button
              onClick={closeSearch}
              className="close-search-btn absolute right-10 w-9 h-9 rounded-full flex items-center justify-center bg-[#F5F5F5] hover:bg-[#EBEBEB] transition-all"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </header>

      {/* Header - 移动端 */}
      <header className="home-header-mobile sticky top-0 z-90 flex md:hidden items-center h-[56px] px-4"
        style={{ background: 'rgba(255, 255, 255, 0.98)' }}
      >
        {!isSearchExpanded ? (
          <>
            {/* 左侧：Logo */}
            <Link to="/" className="mobile-header-logo">
              <BeeIcon size={24} />
            </Link>

            {/* 中间：引流入口 */}
            <div className="promo-entry-mobile flex-1 flex items-center justify-center">
              <AgentPromoBanner />
            </div>

            {/* 右侧：搜索按钮 */}
            <button
              onClick={toggleSearch}
              className="search-toggle-btn-mobile w-8 h-8 rounded-full flex items-center justify-center bg-[#F5F5F5]"
            >
              <SearchIcon size={16} className="text-gray-500" />
            </button>
          </>
        ) : (
          /* 搜索展开状态 - 覆盖整个header */
          <div className="search-expand-mobile flex-1 flex items-center gap-2">
            <div className="search-bar-mobile flex-1 relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" size={16} />
              <input
                type="text"
                className="search-input-mobile w-full py-2 px-3 pl-9 pr-8 border-none rounded-full text-sm bg-[#F5F5F5] outline-none"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            <button
              onClick={closeSearch}
              className="close-search-btn-mobile w-8 h-8 rounded-full flex items-center justify-center bg-[#F5F5F5]"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </header>

      <div className="core-container px-10 pb-10">
      {/* 分类标签页 */}
      <div className="tabs">
        {tabs.map((tab) => (
          <div
            key={tab.key}
            className={`tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* 瀑布流 */}
      {isLoading && filteredData.length === 0 ? (
        <FeedSkeletonGrid />
      ) : filteredData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p>{debouncedSearch ? `No results found for "${debouncedSearch}"` : 'No content yet'}</p>
        </div>
      ) : (
        <>
          <MasonryGrid
            gap={20}
            responsiveColumns={{
              desktop: 5,  // > 1600px
              laptop: 4,   // 1200px - 1600px
              tablet: 3,   // 900px - 1200px
              mobile: 2,   // < 900px
            }}
          >
            {filteredData.map((item) => (
              <FeedCard
                key={item.id}
                id={item.id}
                title={item.title}
                image={item.image}
                aspectRatio={item.aspectRatio}
                author={item.author}
                isAI={item.isAI}
                aiCreator={item.aiCreator}
                likes={item.likes}
                onClick={() => handleCardClick(item.id)}
              />
            ))}
          </MasonryGrid>

          {/* Sentinel 元素 - 用于 IntersectionObserver 监测 */}
          <div ref={sentinelRef} className="h-10" />

          {/* 底部加载状态指示器 */}
          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4 text-gray-400">
              <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Loading...
            </div>
          )}

          {/* No more data indicator */}
          {!hasNextPage && filteredData.length > 0 && (
            <div className="flex justify-center items-center py-4 text-gray-400 text-sm">
              All content loaded
            </div>
          )}
        </>
      )}
    </div>

      {/* 笔记详情弹窗 */}
      <NoteDetailModal
        postId={selectedPostId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  )
}

export default Home